import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { PROJECTS, VIDEOS } from "../data/content";

const ALL_TAGS = ["Tất cả", ...new Set(PROJECTS.flatMap((p) => p.tags))];

/* ── Case Study Card (horizontal) ── */
function ProjectCard({ project, index }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="blog-card-enter"
      style={{
        textDecoration: "none",
        display: "block",
        animationDelay: `${index * 80}ms`,
      }}
    >
      <div
        className="project-card-inner"
        style={{
          gap: 0,
          background: "#fff",
          border: "1px solid #E4E4E7",
          borderRadius: 16,
          overflow: "hidden",
          transition: "transform 280ms cubic-bezier(0.25,0.1,0.25,1), box-shadow 280ms cubic-bezier(0.25,0.1,0.25,1)",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-3px)";
          e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.09)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "";
          e.currentTarget.style.boxShadow = "";
        }}
      >
        {/* Image */}
        <div className="project-card-img">
          {project.thumbnail ? (
            <img
              src={project.thumbnail}
              alt={project.title}
              style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block", transition: "transform 500ms cubic-bezier(0.25,0.1,0.25,1)", position: "absolute", inset: 0 }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.04)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
            />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56 }}>
              {project.emoji}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: "28px 28px 24px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            {/* Number + status */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: 11, fontWeight: 700, color: "#94A3B8",
                letterSpacing: "0.08em",
              }}>
                {String(index + 1).padStart(2, "0")}
              </span>
              <span style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: 10, fontWeight: 700,
                background: "#DCFCE7", color: "#16A34A",
                padding: "2px 8px", borderRadius: 4,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                {project.status}
              </span>
            </div>

            {/* Title */}
            <h3 style={{
              fontFamily: "'Archivo',sans-serif",
              fontWeight: 800, fontSize: 17,
              color: "#09090B", lineHeight: 1.35,
              margin: "0 0 10px",
              letterSpacing: "-0.02em",
            }}>
              {project.title}
            </h3>

            {/* Description */}
            <p style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 13, color: "#64748B",
              lineHeight: 1.65, margin: "0 0 16px",
              display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
              {project.description}
            </p>
          </div>

          {/* Footer: tags + CTA */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {project.tags.map(t => (
                <span key={t} className="tag" style={{ fontSize: 11 }}>{t}</span>
              ))}
            </div>
            <span style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 12, fontWeight: 700, color: "#2563EB",
              display: "flex", alignItems: "center", gap: 3,
            }}>
              Xem chi tiết <ArrowUpRight size={13} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ── Video Card ── */
function VideoCard({ video, index }) {
  const thumbId = video.embedUrl?.split("/embed/")[1];
  return (
    <Link
      to={`/videos/${video.id}`}
      className="blog-card-enter"
      style={{ textDecoration: "none", display: "block", animationDelay: `${index * 70}ms` }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #E4E4E7",
          borderRadius: 14,
          overflow: "hidden",
          transition: "transform 280ms cubic-bezier(0.25,0.1,0.25,1), box-shadow 280ms cubic-bezier(0.25,0.1,0.25,1)",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.10)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "";
          e.currentTarget.style.boxShadow = "";
        }}
      >
        <div style={{ height: 180, overflow: "hidden", background: "#E8ECF0", position: "relative" }}>
          {thumbId ? (
            <>
              <img
                src={`https://img.youtube.com/vi/${thumbId}/hqdefault.jpg`}
                alt={video.title}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <div style={{
                position: "absolute", inset: 0,
                background: "rgba(0,0,0,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Play size={18} color="#09090B" style={{ marginLeft: 2 }} />
                </div>
              </div>
            </>
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48 }}>
              {video.emoji}
            </div>
          )}
        </div>
        <div style={{ padding: "16px 16px 14px" }}>
          <h3 style={{
            fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 14,
            color: "#09090B", lineHeight: 1.4, margin: "0 0 6px",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {video.title}
          </h3>
          <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#94A3B8", margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {video.description}
          </p>
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #F4F4F5", display: "flex", justifyContent: "flex-end" }}>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, fontWeight: 700, color: "#2563EB", display: "flex", alignItems: "center", gap: 2 }}>
              Xem <ArrowUpRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ── Page ── */
export default function Projects() {
  const [activeTag, setActiveTag] = useState("Tất cả");

  const filtered = activeTag === "Tất cả"
    ? PROJECTS
    : PROJECTS.filter((p) => p.tags.includes(activeTag));

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 80px" }}>

      {/* Header */}
      <div className="reveal" style={{ marginBottom: 40 }}>
        <span className="section-label">Dự án</span>
        <h1 style={{
          fontFamily: "'Archivo',sans-serif",
          fontWeight: 900,
          fontSize: "clamp(28px,4vw,52px)",
          color: "#09090B",
          margin: "0 0 10px",
          letterSpacing: "-0.03em",
          lineHeight: 1.05,
        }}>
          Dự án &amp; Thiết kế<br />
          <span style={{ color: "#2563EB" }}>thực tế</span>
        </h1>
        <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#64748B", margin: 0, maxWidth: 460, lineHeight: 1.65 }}>
        </p>
      </div>

      {/* Filter tabs */}
      <div className="reveal reveal-delay-1" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 36 }}>
        {ALL_TAGS.map(tag => {
          const count = tag === "Tất cả" ? PROJECTS.length : PROJECTS.filter(p => p.tags.includes(tag)).length;
          const isActive = activeTag === tag;
          return (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 999,
                border: isActive ? "1.5px solid #09090B" : "1.5px solid #E4E4E7",
                background: isActive ? "#09090B" : "#fff",
                color: isActive ? "#fff" : "#52525B",
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: 13, fontWeight: 600,
                cursor: "pointer",
                transition: "all 180ms cubic-bezier(0.25,0.1,0.25,1)",
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = "#09090B"; e.currentTarget.style.color = "#09090B"; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = "#E4E4E7"; e.currentTarget.style.color = "#52525B"; } }}
            >
              {tag}
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: isActive ? "rgba(255,255,255,0.18)" : "#F4F4F5",
                color: isActive ? "#fff" : "#94A3B8",
                padding: "1px 5px", borderRadius: 99,
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Case study list */}
      <div key={activeTag} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {filtered.map((project, i) => (
          <ProjectCard key={project.id} project={project} index={i} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "64px 0", fontFamily: "'Space Grotesk',sans-serif", color: "#94A3B8", fontSize: 14 }}>
          Chưa có dự án nào trong mục này.
        </div>
      )}

      {/* Video section */}
      {VIDEOS.length > 0 && (
        <div style={{ marginTop: 72 }}>
          <div className="reveal" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, paddingBottom: 12, borderBottom: "1px solid #E4E4E7" }}>
            <div style={{ width: 3, height: 18, background: "#2563EB", borderRadius: 2 }} />
            <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 18, color: "#09090B", margin: 0, letterSpacing: "-0.02em" }}>
              Video
            </h2>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#94A3B8" }}>
              {VIDEOS.length} video
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {VIDEOS.map((video, i) => (
              <VideoCard key={video.id} video={video} index={i} />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
